import React, {
  createContext,
  useEffect,
  FormEvent,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { Modal } from '@/components/Modal/Modal';
import { RatingStars } from '@/components/RatingStars';
import { SVGIcon } from '@/components/SVGIcon';
import feedbackBannerBackground from '@/assets/zostaw-opinie-bg.png';
import type {
  FeedbackContextDto,
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
} from '@/api/types/models.types';
import { APP_COLORS } from '@/config/colors';

type ThumbDirection = NonNullable<SubmitFeedbackRequest['thumb']>;
type RatingKey = 'report' | 'customerService' | 'waitingTime';
type FeedbackModalView = 'ratings' | 'question' | 'success';
type FeedbackPromptContext = 'returning' | 'pdfDownload';

interface CustomerFeedbackProviderProps {
  /** Backend availability context controlling whether feedback may be requested. */
  availability?: FeedbackContextDto;
  /** Enables mandatory feedback for eligible customers returning to the report. */
  mandatoryReturningFeedbackEnabled?: boolean;
  /** Called with the completed feedback payload. */
  onSubmit: (
    data: SubmitFeedbackRequest,
  ) => Promise<SubmitFeedbackResponse> | SubmitFeedbackResponse;
  /** Optional visual asset rendered on the right side of the banner. */
  bannerAssetSrc?: string;
  /** Slot components. */
  children: ReactNode;
}

interface CustomerFeedbackContextValue {
  activePromptContext: FeedbackPromptContext | null;
  bannerAssetSrc?: string;
  errorMessage: string | null;
  isModalOpen: boolean;
  isSuccessModalOpen: boolean;
  isThumbModalOpen: boolean;
  isSubmitting: boolean;
  isReturningFeedbackRequired: boolean;
  ratings: Record<RatingKey, number | null>;
  selectedThumb: ThumbDirection | null;
  shouldShowCta: boolean;
  shouldUseReturningCustomerFlow: boolean;
  submitted: boolean;
  closeForm: () => void;
  closeSuccess: () => void;
  openForm: (thumb: ThumbDirection) => void;
  openPdfDownloadPrompt: (onFeedbackSubmitted: () => void) => void;
  setRating: (key: RatingKey, value: number) => void;
  submitForm: (thumb?: ThumbDirection) => Promise<void>;
}

const CustomerFeedbackContext =
  createContext<CustomerFeedbackContextValue | null>(null);

const ERROR_MESSAGE_AUTO_HIDE_MS = 5000;
const SUCCESS_MODAL_AUTO_HIDE_MS = 2000;
const PDF_DOWNLOAD_AFTER_MODAL_CLOSE_MS = 350;

const ratingRows: Array<{
  key: RatingKey;
  label: string;
  description: string;
  testId: string;
}> = [
  {
    key: 'report',
    label: 'Oględziny / raport',
    description: 'Czy raport spełnił Twoje oczekiwania?',
    testId: 'feedback-rating-report',
  },
  {
    key: 'customerService',
    label: 'Obsługa klienta',
    description: 'Czy jesteś zadowolony z kontaktu z nami?',
    testId: 'feedback-rating-customer-service',
  },
  {
    key: 'waitingTime',
    label: 'Czas oczekiwania',
    description: 'Czy czas realizacji był dla Ciebie odpowiedni?',
    testId: 'feedback-rating-waiting-time',
  },
];

const FEEDBACK_VOTE_BUTTON_SHADOW =
  '0px 0px 14px 7px rgba(220, 197, 150, 0.26)';

const feedbackVoteButtonStyle = {
  background: APP_COLORS.feedback.voteBg,
  borderColor: APP_COLORS.feedback.voteBorder,
  '--tw-shadow': FEEDBACK_VOTE_BUTTON_SHADOW,
  '--tw-shadow-colored': FEEDBACK_VOTE_BUTTON_SHADOW,
} as React.CSSProperties;

const feedbackStickyButtonStyle = {
  backgroundColor: APP_COLORS.feedback.iconBg,
  color: APP_COLORS.feedback.iconThumb,
} as React.CSSProperties;

const feedbackPromptButtonStyle = {
  backgroundColor: APP_COLORS.feedback.iconBg,
  color: APP_COLORS.primary.DEFAULT,
} as React.CSSProperties;

const pdfPromptHeaderStyle = {
  backgroundImage: `linear-gradient(80.38deg, ${APP_COLORS.feedback.pdfHeaderStart} 0%, ${APP_COLORS.feedback.pdfHeaderEnd} 99.64%)`,
} as React.CSSProperties;

const useCustomerFeedback = (): CustomerFeedbackContextValue => {
  const context = useContext(CustomerFeedbackContext);
  if (!context) {
    throw new Error(
      'CustomerFeedback components must be used within CustomerFeedbackProvider',
    );
  }
  return context;
};

const initialRatings: Record<RatingKey, number | null> = {
  report: null,
  customerService: null,
  waitingTime: null,
};

export const CustomerFeedbackProvider: React.FC<
  CustomerFeedbackProviderProps
> = ({
  availability,
  mandatoryReturningFeedbackEnabled = import.meta.env
    .VITE_MANDATORY_RETURNING_FEEDBACK === 'true',
  onSubmit,
  bannerAssetSrc,
  children,
}) => {
  const [selectedThumb, setSelectedThumb] = useState<ThumbDirection | null>(
    null,
  );
  const [ratings, setRatings] =
    useState<Record<RatingKey, number | null>>(initialRatings);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isThumbModalOpen, setIsThumbModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasAutoOpenedReturningFlow, setHasAutoOpenedReturningFlow] =
    useState(false);
  const [shouldAutoCloseSuccess, setShouldAutoCloseSuccess] = useState(false);
  const [activePromptContext, setActivePromptContext] =
    useState<FeedbackPromptContext | null>(null);
  const isSubmittingRef = useRef(false);
  const pdfDownloadCallbackRef = useRef<(() => void) | null>(null);
  const pdfDownloadTimeoutRef = useRef<number | null>(null);

  const shouldShowCta =
    availability?.canVote === true &&
    availability?.alreadyVoted !== true &&
    !submitted;
  const successfulLoginCount = availability?.successfulLoginCount;
  const shouldUseReturningCustomerFlow =
    availability?.canVote === true &&
    availability?.alreadyVoted === false &&
    !submitted &&
    typeof successfulLoginCount === 'number' &&
    Number.isFinite(successfulLoginCount) &&
    successfulLoginCount > 1;
  const isReturningFeedbackRequired =
    mandatoryReturningFeedbackEnabled && shouldUseReturningCustomerFlow;

  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setErrorMessage(null);
    }, ERROR_MESSAGE_AUTO_HIDE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [errorMessage]);

  useEffect(() => {
    if (!isSuccessModalOpen || !shouldAutoCloseSuccess) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsSuccessModalOpen(false);
    }, SUCCESS_MODAL_AUTO_HIDE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isSuccessModalOpen, shouldAutoCloseSuccess]);

  const clearScheduledPdfDownload = useCallback(() => {
    if (pdfDownloadTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(pdfDownloadTimeoutRef.current);
    pdfDownloadTimeoutRef.current = null;
  }, []);

  const runPdfDownloadAfterModalClose = useCallback(
    (callback?: () => void) => {
      if (!callback) {
        return;
      }

      clearScheduledPdfDownload();
      pdfDownloadTimeoutRef.current = window.setTimeout(() => {
        pdfDownloadTimeoutRef.current = null;
        callback();
      }, PDF_DOWNLOAD_AFTER_MODAL_CLOSE_MS);
    },
    [clearScheduledPdfDownload],
  );

  useEffect(() => clearScheduledPdfDownload, [clearScheduledPdfDownload]);

  const openReturningCustomerFlow = useCallback(() => {
    clearScheduledPdfDownload();
    pdfDownloadCallbackRef.current = null;
    setActivePromptContext('returning');
    setSelectedThumb(null);
    setErrorMessage(null);
    setIsModalOpen(false);
    setIsThumbModalOpen(true);
    setIsSuccessModalOpen(false);
    setShouldAutoCloseSuccess(false);
  }, [clearScheduledPdfDownload]);

  useEffect(() => {
    if (!shouldUseReturningCustomerFlow || hasAutoOpenedReturningFlow) {
      return;
    }

    setHasAutoOpenedReturningFlow(true);
    openReturningCustomerFlow();
  }, [
    hasAutoOpenedReturningFlow,
    openReturningCustomerFlow,
    shouldUseReturningCustomerFlow,
  ]);

  const openForm = useCallback(
    (thumb: ThumbDirection) => {
      clearScheduledPdfDownload();
      pdfDownloadCallbackRef.current = null;
      setActivePromptContext(null);
      setSelectedThumb(thumb);
      setErrorMessage(null);
      setIsThumbModalOpen(false);
      setIsSuccessModalOpen(false);
      setShouldAutoCloseSuccess(false);
      setIsModalOpen(true);
    },
    [clearScheduledPdfDownload],
  );

  const openPdfDownloadPrompt = useCallback(
    (onFeedbackSubmitted: () => void) => {
      if (!shouldShowCta) {
        onFeedbackSubmitted();
        return;
      }

      pdfDownloadCallbackRef.current = onFeedbackSubmitted;
      clearScheduledPdfDownload();
      setActivePromptContext('pdfDownload');
      setSelectedThumb(null);
      setErrorMessage(null);
      setIsModalOpen(false);
      setIsThumbModalOpen(true);
      setIsSuccessModalOpen(false);
      setShouldAutoCloseSuccess(false);
    },
    [clearScheduledPdfDownload, shouldShowCta],
  );

  const closeForm = useCallback(() => {
    const isPdfDownloadFlow = activePromptContext === 'pdfDownload';
    const pdfDownloadCallback = pdfDownloadCallbackRef.current;

    if (!isPdfDownloadFlow) {
      clearScheduledPdfDownload();
    }
    pdfDownloadCallbackRef.current = null;
    setActivePromptContext(null);
    setIsModalOpen(false);
    setIsThumbModalOpen(false);
    if (isPdfDownloadFlow) {
      runPdfDownloadAfterModalClose(pdfDownloadCallback);
    }
  }, [
    activePromptContext,
    clearScheduledPdfDownload,
    runPdfDownloadAfterModalClose,
  ]);

  const closeSuccess = useCallback(() => {
    setIsSuccessModalOpen(false);
    setShouldAutoCloseSuccess(false);
  }, []);

  const setRating = useCallback((key: RatingKey, value: number) => {
    setRatings((currentRatings) => ({
      ...currentRatings,
      [key]: value,
    }));
  }, []);

  const submitForm = useCallback(
    async (thumbOverride?: ThumbDirection) => {
      if (isSubmittingRef.current) {
        return;
      }

      const thumb = thumbOverride ?? selectedThumb;

      if (
        thumbOverride &&
        activePromptContext &&
        (!ratings.report || !ratings.customerService || !ratings.waitingTime)
      ) {
        setSelectedThumb(thumbOverride);
        setErrorMessage(null);
        setIsThumbModalOpen(false);
        setIsModalOpen(true);
        return;
      }

      if (!ratings.report || !ratings.customerService || !ratings.waitingTime) {
        return;
      }

      if (!thumb && shouldUseReturningCustomerFlow) {
        setActivePromptContext('returning');
        setIsModalOpen(false);
        setIsThumbModalOpen(true);
        setErrorMessage(null);
        return;
      }

      if (!thumb) {
        return;
      }

      const payload: SubmitFeedbackRequest = {
        thumb,
        report: ratings.report,
        customerService: ratings.customerService,
        waitingTime: ratings.waitingTime,
      };

      const isPdfDownloadFlow = activePromptContext === 'pdfDownload';
      const shouldAutoCloseCurrentSuccess =
        shouldUseReturningCustomerFlow || isThumbModalOpen;
      const pdfDownloadCallback = pdfDownloadCallbackRef.current;

      isSubmittingRef.current = true;
      setSelectedThumb(thumb);
      setIsSubmitting(true);
      try {
        await onSubmit(payload);
        setSubmitted(true);
        setIsModalOpen(false);
        setIsThumbModalOpen(false);
        setShouldAutoCloseSuccess(
          isPdfDownloadFlow ? false : shouldAutoCloseCurrentSuccess,
        );
        setIsSuccessModalOpen(!isPdfDownloadFlow);
        setActivePromptContext(null);
        pdfDownloadCallbackRef.current = null;
        setErrorMessage(null);
        if (isPdfDownloadFlow) {
          runPdfDownloadAfterModalClose(pdfDownloadCallback);
        }
      } catch {
        pdfDownloadCallbackRef.current = null;
        setShouldAutoCloseSuccess(false);
        if (isPdfDownloadFlow) {
          setActivePromptContext(null);
          setIsModalOpen(false);
          setIsThumbModalOpen(false);
          setErrorMessage(null);
          runPdfDownloadAfterModalClose(pdfDownloadCallback);
        } else if (isReturningFeedbackRequired) {
          setErrorMessage('Nie udało się wysłać opinii. Spróbuj ponownie.');
        } else {
          setActivePromptContext(null);
          setIsModalOpen(false);
          setIsThumbModalOpen(false);
          setErrorMessage('Nie udało się wysłać opinii. Spróbuj ponownie.');
        }
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [
      activePromptContext,
      isReturningFeedbackRequired,
      isThumbModalOpen,
      onSubmit,
      ratings,
      runPdfDownloadAfterModalClose,
      selectedThumb,
      shouldUseReturningCustomerFlow,
    ],
  );

  const value = useMemo<CustomerFeedbackContextValue>(
    () => ({
      activePromptContext,
      bannerAssetSrc,
      closeSuccess,
      closeForm,
      errorMessage,
      isModalOpen,
      isSuccessModalOpen,
      isThumbModalOpen,
      isSubmitting,
      isReturningFeedbackRequired,
      openForm,
      openPdfDownloadPrompt,
      ratings,
      selectedThumb,
      setRating,
      shouldShowCta,
      shouldUseReturningCustomerFlow,
      submitForm,
      submitted,
    }),
    [
      activePromptContext,
      bannerAssetSrc,
      closeForm,
      closeSuccess,
      errorMessage,
      isModalOpen,
      isSuccessModalOpen,
      isThumbModalOpen,
      isSubmitting,
      isReturningFeedbackRequired,
      openForm,
      openPdfDownloadPrompt,
      ratings,
      selectedThumb,
      setRating,
      shouldShowCta,
      shouldUseReturningCustomerFlow,
      submitForm,
      submitted,
    ],
  );

  return (
    <CustomerFeedbackContext.Provider value={value}>
      {children}
    </CustomerFeedbackContext.Provider>
  );
};

interface ThumbButtonProps {
  thumb: ThumbDirection;
  selected?: boolean;
  compact?: boolean;
  prompt?: boolean;
  largePrompt?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const ThumbButton = React.forwardRef<HTMLButtonElement, ThumbButtonProps>(
  (
    {
      thumb,
      selected = false,
      compact = false,
      prompt = false,
      largePrompt = false,
      disabled = false,
      onClick,
    },
    ref,
  ) => {
    const isPositive = thumb === 'UP';
    const iconSize = largePrompt ? 32 : prompt ? 24 : compact ? 18 : 32;

    return (
      <button
        ref={ref}
        type="button"
        aria-label={isPositive ? 'Pozytywna opinia' : 'Negatywna opinia'}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        style={
          prompt
            ? feedbackPromptButtonStyle
            : compact
              ? feedbackStickyButtonStyle
              : !selected
                ? feedbackVoteButtonStyle
                : undefined
        }
        className={clsx(
          'inline-flex shrink-0 items-center justify-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70',
          prompt
            ? largePrompt
              ? 'h-[76px] w-[76px]'
              : 'h-14 w-14'
            : compact
              ? 'h-10 w-10'
              : 'relative h-[76px] w-[76px] gap-2.5 overflow-hidden p-1',
          selected
            ? 'border-primary bg-primary text-white'
            : prompt
              ? 'border-0 bg-feedback-iconBg text-primary hover:bg-feedback-iconBgHover'
              : compact
                ? 'border-0 bg-feedback-iconBg text-feedback-iconThumb hover:bg-feedback-iconBgHover'
                : 'border-feedback-voteBorder bg-feedback-voteBg text-white shadow shadow-feedbackVote',
        )}
      >
        <SVGIcon
          name={isPositive ? 'thumb-up' : 'thumb-down'}
          size={iconSize}
          fill={
            selected || (!compact && !prompt)
              ? 'white'
              : prompt
                ? 'primary'
                : 'feedback-iconThumb'
          }
          aria-hidden="true"
        />
      </button>
    );
  },
);

ThumbButton.displayName = 'ThumbButton';

export const CustomerFeedbackBanner: React.FC = () => {
  const { bannerAssetSrc, openForm, shouldShowCta } = useCustomerFeedback();

  if (!shouldShowCta) {
    return null;
  }

  return (
    <section
      data-testid="customer-feedback-banner"
      className="mx-auto my-5 mt-0 flex min-h-[200px] w-full max-w-[1676px] overflow-hidden rounded-[22px] bg-navy-950 bg-no-repeat px-8 py-8 text-white shadow-card lg:bg-[image:var(--feedback-banner-bg)] lg:bg-[length:auto_100%] lg:bg-right lg:px-12 print:hidden"
      aria-label="Opinia o raporcie"
      style={
        {
          '--feedback-banner-bg': `url(${feedbackBannerBackground})`,
        } as React.CSSProperties
      }
    >
      <div className="flex w-full flex-col gap-8 lg:flex-row lg:items-center">
        <div className="max-w-[760px] shrink-0">
          <p className="inline-flex rounded-full bg-white px-4 py-1 font-archivo text-xs font-bold uppercase leading-none tracking-[0.3px] text-navy-950">
            TWOJA OPINIA MA ZNACZENIE
          </p>
          <p className="mt-3 font-archivo text-xl font-bold leading-tight text-white lg:text-2xl">
            Czy poleciłbyś naszą usługę lub skorzystał z niej ponownie?
          </p>
        </div>
        <div className="flex items-center gap-5 lg:ml-8">
          <ThumbButton thumb="UP" onClick={() => openForm('UP')} />
          <ThumbButton thumb="DOWN" onClick={() => openForm('DOWN')} />
        </div>
      </div>
      {bannerAssetSrc && (
        <img
          src={bannerAssetSrc}
          alt=""
          className="ml-6 hidden h-[152px] w-auto self-center object-contain lg:block"
        />
      )}
    </section>
  );
};

export const CustomerFeedbackSticky: React.FC = () => {
  const { openForm, shouldShowCta } = useCustomerFeedback();

  if (!shouldShowCta) {
    return null;
  }

  return (
    <aside
      data-testid="customer-feedback-sticky"
      className="fixed bottom-6 right-0 z-40 flex h-16 -translate-x-1/2 items-center rounded-[16px] border border-feedback-cardBorder bg-white py-3 pl-6 pr-3 shadow-feedback desktop:right-6 desktop:translate-x-0 print:hidden"
      aria-label="Szybka opinia o raporcie"
    >
      <div className="flex items-center gap-5">
        <p className="whitespace-nowrap font-archivo text-base font-semibold leading-[30px] text-text-primary">
          Poleciłbyś nas?
        </p>
        <div className="flex items-center gap-3">
          <ThumbButton thumb="UP" compact onClick={() => openForm('UP')} />
          <ThumbButton thumb="DOWN" compact onClick={() => openForm('DOWN')} />
        </div>
      </div>
    </aside>
  );
};

interface CustomerFeedbackPdfDownloadGateProps {
  onDownloadPdf: () => void;
  children: (onDownloadPdf: () => void) => ReactNode;
}

export const CustomerFeedbackPdfDownloadGate: React.FC<
  CustomerFeedbackPdfDownloadGateProps
> = ({ onDownloadPdf, children }) => {
  const { openPdfDownloadPrompt } = useCustomerFeedback();

  const handleDownloadPdf = useCallback(() => {
    openPdfDownloadPrompt(onDownloadPdf);
  }, [onDownloadPdf, openPdfDownloadPrompt]);

  return <>{children(handleDownloadPdf)}</>;
};

interface CustomerFeedbackPdfPromptProps {
  firstThumbButtonRef: React.RefObject<HTMLButtonElement | null>;
  isSubmitting: boolean;
  onThumbSelect: (thumb: ThumbDirection) => void;
}

const CustomerFeedbackPdfPrompt: React.FC<CustomerFeedbackPdfPromptProps> = ({
  firstThumbButtonRef,
  isSubmitting,
  onThumbSelect,
}) => (
  <div
    data-testid="customer-feedback-pdf-prompt"
    className="flex min-h-[366px] flex-col overflow-hidden font-inter print:hidden"
  >
    <div
      className="relative h-[90px] overflow-hidden"
      style={pdfPromptHeaderStyle}
    >
      <div
        data-testid="customer-feedback-pdf-icon"
        className="absolute left-4 top-0 flex h-[90px] w-[99px] items-center justify-center opacity-25"
      >
        <SVGIcon
          name="document-pdf"
          size={90}
          fill="feedback-pdfIcon"
          aria-hidden="true"
        />
      </div>
      <div className="absolute left-1/2 top-9 flex h-6 w-[min(74%,310px)] -translate-x-1/2 items-center overflow-hidden rounded-button border border-progressBar-border bg-progressBar-bg">
        <span
          aria-hidden="true"
          className="absolute inset-y-px left-px w-[38%] rounded-l-button bg-progressBar-fill"
        />
        <span className="relative z-10 w-full text-center font-inter text-xs font-bold uppercase leading-5 tracking-[0.5px] text-white">
          TWÓJ RAPORT PDF JEST GENEROWANY
        </span>
      </div>
    </div>
    <div className="flex flex-1 flex-col items-center px-6 pb-7 pt-8 text-center">
      <p className="max-w-[260px] text-sm leading-[18px] text-grayText">
        Zanim pobierzesz raport PDF - odpowiedz na krótkie pytanie
      </p>
      <p className="mt-5 max-w-[360px] font-archivo text-[22px] font-bold leading-[30px] text-text-primary">
        Czy poleciłbyś naszą usługę lub skorzystał z niej ponownie?
      </p>
      <div className="mt-6 flex items-center justify-center gap-4">
        <ThumbButton
          ref={firstThumbButtonRef}
          thumb="UP"
          prompt
          largePrompt
          disabled={isSubmitting}
          onClick={() => onThumbSelect('UP')}
        />
        <ThumbButton
          thumb="DOWN"
          prompt
          largePrompt
          disabled={isSubmitting}
          onClick={() => onThumbSelect('DOWN')}
        />
      </div>
    </div>
  </div>
);

export const CustomerFeedbackModalHost: React.FC = () => {
  const {
    activePromptContext,
    closeForm,
    closeSuccess,
    errorMessage,
    isModalOpen,
    isSuccessModalOpen,
    isThumbModalOpen,
    isSubmitting,
    isReturningFeedbackRequired,
    ratings,
    selectedThumb,
    setRating,
    shouldUseReturningCustomerFlow,
    submitForm,
    submitted,
  } = useCustomerFeedback();

  const firstRatingStarRef = useRef<HTMLButtonElement>(null);
  const firstThumbButtonRef = useRef<HTMLButtonElement>(null);

  const areRatingsComplete =
    Boolean(ratings.report) &&
    Boolean(ratings.customerService) &&
    Boolean(ratings.waitingTime);
  const isComplete =
    areRatingsComplete &&
    (Boolean(selectedThumb) || shouldUseReturningCustomerFlow);
  const isSuccessVisible = submitted && isSuccessModalOpen;
  const isQuestionVisible = isThumbModalOpen && !isSuccessVisible;
  const activeModalView: FeedbackModalView | null = isSuccessVisible
    ? 'success'
    : isQuestionVisible
      ? 'question'
      : isModalOpen
        ? 'ratings'
        : null;
  const [renderedModalView, setRenderedModalView] =
    useState<FeedbackModalView | null>(activeModalView);
  const [renderedPromptContext, setRenderedPromptContext] =
    useState<FeedbackPromptContext | null>(activePromptContext);
  const displayedModalView = activeModalView ?? renderedModalView;
  const isDisplayedSuccess = displayedModalView === 'success';
  const isDisplayedQuestion = displayedModalView === 'question';
  const displayedPromptContext = isQuestionVisible
    ? activePromptContext
    : renderedPromptContext;
  const isDisplayedPdfQuestion =
    isDisplayedQuestion && displayedPromptContext === 'pdfDownload';
  const modalTitle = isDisplayedSuccess
    ? 'Dziękujemy'
    : isDisplayedQuestion
      ? 'Czy poleciłbyś naszą usługę lub skorzystał z niej ponownie?'
      : 'Oceń poszczególne elementy usługi';
  const handleModalClose = isSuccessVisible ? closeSuccess : closeForm;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submitForm();
  };

  useEffect(() => {
    if (activeModalView) {
      setRenderedModalView(activeModalView);
    }
  }, [activeModalView]);

  useEffect(() => {
    if (activeModalView === 'question') {
      setRenderedPromptContext(activePromptContext);
    }
  }, [activeModalView, activePromptContext]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      firstRatingStarRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isQuestionVisible) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      firstThumbButtonRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isQuestionVisible]);

  return (
    <>
      {errorMessage && !isReturningFeedbackRequired && (
        <div
          role="alert"
          className="fixed bottom-20 left-1/2 z-40 w-[min(92vw,460px)] -translate-x-1/2 rounded-card border border-red-200 bg-white px-4 py-3 font-inter text-sm text-text-primary shadow-card print:hidden"
        >
          {errorMessage}
        </div>
      )}
      <Modal
        isOpen={Boolean(activeModalView)}
        isDismissible={
          !isReturningFeedbackRequired || activePromptContext === 'pdfDownload'
        }
        onClose={handleModalClose}
        title={modalTitle}
        ariaLabel={modalTitle}
        initialFocusRef={
          displayedModalView === 'ratings' ? firstRatingStarRef : undefined
        }
        hideHeader
        size={
          isDisplayedPdfQuestion
            ? 480
            : isDisplayedSuccess || isDisplayedQuestion
              ? 430
              : 460
        }
        overlayClassName="px-4 print:hidden desktop:px-0"
        className={clsx(
          'overflow-hidden rounded-[25px]',
          isDisplayedPdfQuestion
            ? 'min-h-[366px] !p-0'
            : isDisplayedSuccess
              ? 'min-h-[190px] p-8'
              : isDisplayedQuestion
                ? 'min-h-[190px] p-8'
                : 'min-h-[367px] p-6',
          !isDisplayedSuccess &&
            !isDisplayedPdfQuestion &&
            'border border-primary-100 shadow-[2px_5px_24px_7px_rgba(58,58,58,0.6)]',
          isDisplayedPdfQuestion &&
            'shadow-[2px_5px_24px_7px_rgba(58,58,58,0.6)]',
          !isDisplayedSuccess && !isDisplayedQuestion && '!p-0',
        )}
      >
        {isDisplayedSuccess ? (
          <div className="flex min-h-[126px] flex-col items-center justify-center text-center font-inter">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border-4 border-green-200 bg-green-700 text-2xl font-bold text-white">
              <span aria-hidden="true">✓</span>
            </div>
            <p className="mt-4 font-archivo text-2xl font-bold leading-tight text-text-primary">
              Dziękujemy
            </p>
            <p className="mt-3 max-w-[260px] text-sm leading-6 text-grayText">
              Twoja opinia realnie pomaga nam podnosić jakość naszych usług
            </p>
          </div>
        ) : isDisplayedQuestion ? (
          isDisplayedPdfQuestion ? (
            <CustomerFeedbackPdfPrompt
              firstThumbButtonRef={firstThumbButtonRef}
              isSubmitting={isSubmitting}
              onThumbSelect={(thumb) => {
                void submitForm(thumb);
              }}
            />
          ) : (
            <div className="flex min-h-[126px] flex-col items-center justify-center text-center font-inter">
              <p className="max-w-[320px] font-archivo text-lg font-bold leading-7 text-text-primary">
                Czy poleciłbyś naszą usługę lub skorzystał z niej ponownie?
              </p>
              <div className="mt-5 flex items-center justify-center gap-4">
                <ThumbButton
                  ref={firstThumbButtonRef}
                  thumb="UP"
                  prompt
                  disabled={isSubmitting}
                  onClick={() => {
                    void submitForm('UP');
                  }}
                />
                <ThumbButton
                  thumb="DOWN"
                  prompt
                  disabled={isSubmitting}
                  onClick={() => {
                    void submitForm('DOWN');
                  }}
                />
              </div>
            </div>
          )
        ) : (
          <form
            className="flex flex-col font-inter print:hidden"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-9 px-6 pb-7 pt-6">
              <div className="flex flex-col gap-2">
                <p className="font-archivo text-xl font-bold leading-7 text-text-primary">
                  Oceń poszczególne elementy usługi
                </p>
                <p className="text-sm leading-5 text-grayText">
                  Zaznacz od 1 do 5 gwiazdek w każdej kategorii.
                </p>
              </div>

              <div className="flex flex-col gap-[11px]">
                {ratingRows.map((row, index) => (
                  <div
                    key={row.key}
                    data-testid={row.testId}
                    className="flex w-full items-center gap-5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-archivo text-base font-semibold leading-5 text-text-primary">
                        {row.label}
                      </p>
                      <p className="mt-2 text-xs leading-5 text-grayText">
                        {row.description}
                      </p>
                    </div>
                    <RatingStars
                      value={ratings[row.key]}
                      ariaLabel={row.label}
                      onChange={(value) => setRating(row.key, value)}
                      size="sm"
                      className="shrink-0"
                      inactiveColor={APP_COLORS.primary[50]}
                      firstStarRef={
                        index === 0 ? firstRatingStarRef : undefined
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col items-center border-t border-primary-100 px-6 py-5">
              {errorMessage && isReturningFeedbackRequired && (
                <div
                  role="alert"
                  className="mb-4 w-full rounded-card border border-red-200 bg-white px-4 py-3 text-sm text-text-primary shadow-card"
                >
                  {errorMessage}
                </div>
              )}
              <button
                type="submit"
                disabled={!isComplete || isSubmitting}
                className="flex h-10 w-44 items-center justify-center rounded-button bg-primary font-archivo text-sm font-medium leading-[18px] tracking-[0.3px] text-white transition hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-feedback-submitIdle disabled:text-white"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Wyślij opinię'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};
